import type { components } from '@multaqa/api-client'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

export type CustomerInvoice = components['schemas']['InvoiceResource']
export type CustomerPayment = components['schemas']['PaymentResource']
export type StatementType = 'open_item' | 'balance_forward' | 'transaction_statement'

const customerStatementSchema = z.object({
  header: z
    .object({
      type: z.enum(['open_item', 'balance_forward', 'transaction_statement']),
      columns: z.array(z.string()),
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
    })
    .passthrough(),
  rows: z.array(z.record(z.string(), z.string().nullable())),
  summary: z
    .object({
      aging: z.record(z.string(), z.string()).optional(),
      opening_balance: z.string().optional(),
    })
    .passthrough()
    .optional(),
  currency: z.string(),
})

export type CustomerStatement = z.infer<typeof customerStatementSchema>

function unwrap<T>(response: { data?: unknown }): T {
  const envelope = response.data as { data?: unknown } | undefined
  return (envelope?.data ?? response.data) as T
}

// Same key and same queryFn as `useStatementSettings` in accounting-statements:
// a shared key must store the same shape, so this stores the whole settings object
// and selects the one field this feature reads.
export function useStatementDefaultType(companyId: string | null) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'settings'],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response = await api.GET('/accounting/settings', {
        headers: { 'X-Company-Id': companyId ?? '' },
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to load accounting settings')
      return unwrap<components['schemas']['AccountingSettingsResource']>(response.data ?? {})
    },
    select: (settings) => settings.statement_default_type,
  })
}

export function useCustomerInvoices(companyId: string | null, customerId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'invoices', { customer: customerId }],
    enabled: Boolean(companyId && customerId),
    queryFn: async (): Promise<CustomerInvoice[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/invoices', {
        headers: { 'X-Company-Id': companyId },
        params: { query: { customer: customerId } },
      })
      if (!response.data) throw new Error('Customer invoices returned no body')
      return response.data.data
    },
  })
}

export function useCustomerPayments(companyId: string | null, customerId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'payments', 'customer', customerId],
    enabled: Boolean(companyId && customerId),
    queryFn: async (): Promise<CustomerPayment[]> => {
      if (!companyId) return []
      const rows: CustomerPayment[] = []
      let page = 1
      let lastPage = 1
      while (page <= lastPage) {
        const response = await api.GET('/accounting/payments', {
          headers: { 'X-Company-Id': companyId },
          params: {
            query: { partner: customerId, payment_direction: 'inbound', page, per_page: 100 },
          },
        })
        if (!response.data) throw new Error('Customer payments returned no body')
        rows.push(...response.data.data)
        lastPage = response.data.meta.last_page
        page += 1
      }
      return rows
    },
  })
}

// Its own key, not the Statements screen's: this hook stores the response after
// a Zod parse that drops unlisted keys, so sharing a key would hand the other
// screen a trimmed object (docs/notes/frontend-conventions.md).
export function useCustomerStatement(
  companyId: string | null,
  customerId: string,
  type: StatementType,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: [
      'companies',
      companyId,
      'customers',
      'statement',
      customerId,
      type,
      startDate,
      endDate,
    ],
    enabled: Boolean(companyId && customerId && startDate && endDate),
    queryFn: async (): Promise<CustomerStatement> => {
      if (!companyId) throw new Error('Customer statement needs a company')
      const response = await api.GET('/accounting/customers/{customer}/statement', {
        headers: { 'X-Company-Id': companyId },
        params: {
          path: { customer: customerId },
          query: { start_date: startDate, end_date: endDate, type },
        },
      })
      if (!response.data) throw new Error('Customer statement returned no body')
      return customerStatementSchema.parse(response.data.data as unknown)
    },
  })
}
